import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader, Button, Spinner, Alert, Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { APIClient } from '../../../helpers/api_helper';
import CreateEndpointModal from './CreateEndpointModal';
import CodeExamplesModal from './CodeExamplesModal';
import EndpointHelpModal from './EndpointHelpModal';

interface DocProcessor {
    processorId: number;
    companyId: number;
    projectId: number;
    title: string;
    description: string | null;
    endpointSlug: string;
    jsonFormatData: string;
    prompt: string;
    llmId: number;
    status: number;
    sourceType?: number;
    envId?: number;
    version?: number | null;
    linkedProcessorId?: number | null;
    publishedDate?: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

interface DocProcessorVersion {
    id: number;
    processorId: number;
    companyId: number;
    projectId: number;
    title: string;
    description: string | null;
    endpointSlug: string;
    jsonFormatData: string;
    prompt: string;
    llmId: number;
    status: number;
    envId: number;
    version: number;
    publishedDate: string;
    createdAt: string;
    updatedAt: string;
}

interface ProductionEndpointsTableProps {
    projectId: string | undefined;
}

const ProductionEndpointsTable: React.FC<ProductionEndpointsTableProps> = ({ projectId }) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [docProcessors, setDocProcessors] = useState<DocProcessor[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedEndpoint, setSelectedEndpoint] = useState<DocProcessor | null>(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [endpointToDelete, setEndpointToDelete] = useState<DocProcessor | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isInactivating, setIsInactivating] = useState(false);
    const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
    const [endpointForVersions, setEndpointForVersions] = useState<DocProcessor | null>(null);
    const [versions, setVersions] = useState<DocProcessorVersion[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<DocProcessorVersion | null>(null);
    const [isLoadingVersions, setIsLoadingVersions] = useState(false);
    const [isCodeExamplesModalOpen, setIsCodeExamplesModalOpen] = useState(false);
    const [endpointForCodeExamples, setEndpointForCodeExamples] = useState<DocProcessor | null>(null);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

    const apiClient = useMemo(() => new APIClient(), []);

    const handleViewEndpoint = (endpoint: DocProcessor) => {
        setSelectedEndpoint(endpoint);
        setIsViewModalOpen(true);
    };

    const handleDeleteEndpoint = (endpoint: DocProcessor) => {
        setEndpointToDelete(endpoint);
        setIsDeleteModalOpen(true);
    };

    const handleViewVersions = async (endpoint: DocProcessor) => {
        setEndpointForVersions(endpoint);
        setIsVersionModalOpen(true);
        setSelectedVersion(null);
        await fetchVersions(endpoint.processorId);
    };

    const handleShowCodeExamples = (endpoint: DocProcessor) => {
        setEndpointForCodeExamples(endpoint);
        setIsCodeExamplesModalOpen(true);
    };

    const closeCodeExamplesModal = () => {
        setIsCodeExamplesModalOpen(false);
        setEndpointForCodeExamples(null);
    };

    const fetchVersions = async (processorId: number) => {
        setIsLoadingVersions(true);
        try {
            const response = await apiClient.get(`/docprocessor/${processorId}/version`);
            console.log('Versions API Response:', response);
            
            const data = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
            setVersions(data);
            
            // Auto-select first version if available
            if (data.length > 0) {
                setSelectedVersion(data[0]);
            }
        } catch (err) {
            console.error('Error fetching versions:', err);
            setVersions([]);
            setSelectedVersion(null);
        } finally {
            setIsLoadingVersions(false);
        }
    };

    const handleSelectVersion = (version: DocProcessorVersion) => {
        if (selectedVersion?.id === version.id) return; // Already selected
        setSelectedVersion(version);
    };

    const confirmDeleteEndpoint = async () => {
        if (!endpointToDelete) return;
        
        setIsDeleting(true);
        try {
            console.log('Deleting endpoint:', endpointToDelete.processorId);
            await apiClient.delete(`/docprocessor/${endpointToDelete.processorId}`);
            
            await fetchDocProcessors();
            setIsDeleteModalOpen(false);
            setEndpointToDelete(null);
        } catch (error) {
            console.error('Error deleting endpoint:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setEndpointToDelete(null);
    };

    const handleInactiveEndpoint = async () => {
        if (!endpointToDelete) return;
        
        setIsInactivating(true);
        try {
            console.log('Inactivating endpoint:', endpointToDelete.processorId);
            await apiClient.put(`/docprocessor/${endpointToDelete.processorId}`, { status: 0 });
            
            await fetchDocProcessors();
            setIsDeleteModalOpen(false);
            setEndpointToDelete(null);
        } catch (error) {
            console.error('Error inactivating endpoint:', error);
        } finally {
            setIsInactivating(false);
        }
    };

    const handleCreateEndpoint = () => {
        setSelectedEndpoint(null);
        setIsCreateModalOpen(true);
    };

    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedEndpoint(null);
    };

    const closeVersionModal = () => {
        setIsVersionModalOpen(false);
        setEndpointForVersions(null);
        setVersions([]);
        setSelectedVersion(null);
    };

    const fetchDocProcessors = async () => {
        if (!projectId) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            // Fetch production endpoints (envId = 6)
            const response = await apiClient.get(`/docprocessor/project/${projectId}/6`);
            console.log('API Response:', response);
            
            const data = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
            setDocProcessors(data);
        } catch (err) {
            console.error('Error fetching doc processors:', err);
            setError('Failed to load endpoints. Please try again.');
            setDocProcessors([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDocProcessors();
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleModalClose = () => {
        setIsCreateModalOpen(false);
        setSelectedEndpoint(null);
        fetchDocProcessors();
    };

    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatJsonData = (jsonString: string | null) => {
        if (!jsonString) return 'No format data';
        try {
            return JSON.stringify(JSON.parse(jsonString), null, 2);
        } catch {
            return jsonString;
        }
    };

    const getStatusBadge = (status: any) => {
        if (typeof status === 'number') {
            switch (status) {
                case 0:
                    return <Badge color="secondary">Inactive</Badge>;
                case 1:
                    return <Badge color="success">Active</Badge>;
                case 2:
                    return <Badge color="primary">Published</Badge>;
                default:
                    return <Badge color="info">Unknown ({status})</Badge>;
            }
        }
        
        const statusStr = typeof status === 'string' ? status.toLowerCase() : String(status || '').toLowerCase();
        
        switch (statusStr) {
            case 'inactive':
            case '0':
                return <Badge color="secondary">Inactive</Badge>;
            case 'active':
            case '1':
                return <Badge color="success">Active</Badge>;
            case 'published':
            case '2':
                return <Badge color="primary">Published</Badge>;
            case 'error':
                return <Badge color="danger">Error</Badge>;
            case 'pending':
                return <Badge color="warning">Pending</Badge>;
            default:
                return <Badge color="info">{status || 'Unknown'}</Badge>;
        }
    };

    const renderTableContent = () => {
        if (isLoading) {
            return (
                <div className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="text-muted mt-2">Loading production endpoints...</p>
                </div>
            );
        }

        if (error) {
            return (
                <Alert color="danger" className="mb-0">
                    <div className="d-flex align-items-center">
                        <i className="ri-error-warning-line me-2"></i>
                        {error}
                        <Button 
                            color="link" 
                            size="sm" 
                            onClick={fetchDocProcessors}
                            className="ms-auto text-decoration-none"
                        >
                            <i className="ri-refresh-line me-1"></i>
                            Retry
                        </Button>
                    </div>
                </Alert>
            );
        }

        if (docProcessors.length === 0) {
            return (
                <div className="text-center py-5">
                    <div className="mb-3">
                        <i className="ri-file-list-2-line text-muted" style={{ fontSize: '48px' }}></i>
                    </div>
                    <h5 className="text-muted">No production endpoints published yet</h5>
                    <p className="text-muted">Production endpoints will appear here when published from sandbox.</p>
                </div>
            );
        }

        return (
            <div className="table-responsive">
                <Table className="table-nowrap align-middle mb-0">
                    <thead>
                        <tr>
                            <th scope="col">Actions</th>
                            <th scope="col">Title</th>
                            <th scope="col">Endpoint Slug</th>
                            <th scope="col">API Endpoint</th>
                            <th scope="col">Status</th>
                            <th scope="col">Version</th>
                            <th scope="col">Published Date</th>
                            <th scope="col">Created</th>
                            <th scope="col">Last Updated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {docProcessors.map((processor) => (
                            <tr key={processor.processorId}>
                                <td>
                                    <div className="d-flex gap-2">
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            outline
                                            title="View Code Examples"
                                            onClick={() => handleShowCodeExamples(processor)}
                                        >
                                            <i className="ri-code-s-slash-line"></i>
                                        </Button>
                                        <Button
                                            color="info"
                                            size="sm"
                                            outline
                                            title="View Details"
                                            onClick={() => handleViewEndpoint(processor)}
                                        >
                                            <i className="ri-eye-line"></i>
                                        </Button>
                                        <Button
                                            color="primary"
                                            size="sm"
                                            outline
                                            title="View Versions"
                                            onClick={() => handleViewVersions(processor)}
                                        >
                                            <i className="ri-git-branch-line"></i>
                                        </Button>
                                        <Button
                                            color="danger"
                                            size="sm"
                                            outline
                                            title="Delete Endpoint"
                                            onClick={() => handleDeleteEndpoint(processor)}
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </Button>
                                    </div>
                                </td>
                                <td>
                                    <div>
                                        <h6 className="mb-1">{processor.title || 'Untitled'}</h6>
                                        <p className="text-muted mb-0 text-truncate" style={{ maxWidth: '200px' }}>
                                            {processor.description || 'No description'}
                                        </p>
                                    </div>
                                </td>
                                <td>
                                    <code className="bg-light px-2 py-1 rounded">
                                        {processor.endpointSlug}
                                    </code>
                                </td>
                                <td>
                                    <code className="text-primary">
                                        {`/v1/flow/${processor.endpointSlug}`}
                                    </code>
                                </td>
                                <td>
                                    {getStatusBadge(processor.status)}
                                </td>
                                <td>
                                    {processor.version ? (
                                        <Badge color="primary" className="badge-soft">
                                            v{processor.version}
                                        </Badge>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {processor.publishedDate ? formatDate(processor.publishedDate) : 'Not published'}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {processor.createdAt ? formatDate(processor.createdAt) : 'N/A'}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-muted">
                                        {processor.updatedAt ? formatDate(processor.updatedAt) : 'N/A'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <>
            <Card className="card-animate border-success">
                <CardHeader className="d-flex align-items-center">
                    <div className="flex-grow-1">
                        <h4 className="card-title mb-0">
                            🟢 Production Endpoints
                            {!isLoading && (
                                <span className="badge bg-soft-success text-success ms-2">
                                    {docProcessors.length}
                                </span>
                            )}
                        </h4>
                        <p className="text-muted mb-0 mt-1">
                            <small><strong>Base URL:</strong> <code>{process.env.REACT_APP_INTEGR_API_URL || 'https://api.integr.dev'}</code></small>
                        </p>
                    </div>
                    <div className="flex-shrink-0 d-flex gap-2">
                        <Button
                            color="light"
                            onClick={fetchDocProcessors}
                            disabled={isLoading}
                            title="Refresh"
                        >
                            <i className={`ri-refresh-line ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}></i>
                        </Button>
                        <Button
                            color="info"
                            outline
                            onClick={() => setIsHelpModalOpen(true)}
                            title="How to create endpoints"
                        >
                            <i className="ri-question-line"></i>
                        </Button>
                        <Button
                            color="success"
                            onClick={handleCreateEndpoint}
                            className="add-btn"
                        >
                            <i className="ri-add-line align-bottom me-1"></i>
                            Create New Endpoint
                        </Button>
                    </div>
                </CardHeader>
                <CardBody className="p-0">
                    {renderTableContent()}
                </CardBody>
            </Card>
            
            {/* Create Endpoint Modal */}
            <CreateEndpointModal
                isOpen={isCreateModalOpen}
                toggle={handleModalClose}
                projectId={projectId}
                envId={2} // Production environment
                editData={null}
                isEditMode={false}
            />
            
            {/* View Endpoint Modal */}
            <Modal isOpen={isViewModalOpen} toggle={closeViewModal} size="lg" centered>
                <ModalHeader toggle={closeViewModal}>
                    <i className="ri-eye-line me-2"></i>
                    Production Endpoint Details
                </ModalHeader>
                <ModalBody>
                    {selectedEndpoint && (
                        <div>
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Title</label>
                                        <div className="bg-light p-2 rounded">
                                            {selectedEndpoint.title || 'N/A'}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Status</label>
                                        <div className="bg-light p-2 rounded">
                                            {getStatusBadge(selectedEndpoint.status)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Endpoint Slug</label>
                                        <div className="bg-light p-2 rounded">
                                            <code>{selectedEndpoint.endpointSlug}</code>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Version</label>
                                        <div className="bg-light p-2 rounded">
                                            {selectedEndpoint.version ? `v${selectedEndpoint.version}` : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Description</label>
                                <div className="bg-light p-2 rounded">
                                    {selectedEndpoint.description || 'No description provided'}
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-semibold">API Endpoint</label>
                                <div className="bg-light p-2 rounded">
                                    <code className="text-primary">
                                        {`${process.env.REACT_APP_INTEGR_API_URL || 'https://api.integr.dev'}/v1/flow/${selectedEndpoint.endpointSlug}`}
                                    </code>
                                </div>
                            </div>
                            
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Published Date</label>
                                        <div className="bg-light p-2 rounded">
                                            {selectedEndpoint.publishedDate ? formatDate(selectedEndpoint.publishedDate) : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Last Updated</label>
                                        <div className="bg-light p-2 rounded">
                                            {selectedEndpoint.updatedAt ? formatDate(selectedEndpoint.updatedAt) : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-semibold">LLM Configuration</label>
                                <div className="bg-light p-2 rounded">
                                    LLM ID: {selectedEndpoint.llmId}
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-semibold">Prompt</label>
                                <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                                        {selectedEndpoint.prompt || 'No prompt configured'}
                                    </pre>
                                </div>
                            </div>
                            
                            <div className="mb-3">
                                <label className="form-label fw-semibold">JSON Format Data</label>
                                <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                                        {formatJsonData(selectedEndpoint.jsonFormatData)}
                                    </pre>
                                </div>
                            </div>
                            
                            <div className="alert alert-info">
                                <i className="ri-information-line me-2"></i>
                                <strong>Production Environment:</strong> This endpoint is in production and cannot be edited. To make changes, edit the sandbox version and republish.
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeViewModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Versions Modal */}
            <Modal isOpen={isVersionModalOpen} toggle={closeVersionModal} size="xl" centered>
                <ModalHeader toggle={closeVersionModal}>
                    <i className="ri-git-branch-line me-2"></i>
                    Version History - {endpointForVersions?.title}
                </ModalHeader>
                <ModalBody>
                    <div className="row">
                        {/* Left side - Version list */}
                        <div className="col-md-3">
                            <div className="border-end pe-3">
                                <h6 className="mb-3">Available Versions</h6>
                                {isLoadingVersions ? (
                                    <div className="text-center py-4">
                                        <Spinner color="primary" size="sm" />
                                        <p className="text-muted mt-2 mb-0">Loading versions...</p>
                                    </div>
                                ) : versions.length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="ri-git-branch-line text-muted" style={{ fontSize: '24px' }}></i>
                                        <p className="text-muted mt-2 mb-0">No versions found</p>
                                    </div>
                                ) : (
                                    <div className="version-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        {versions.map((version) => (
                                            <div 
                                                key={version.id}
                                                className={`p-3 border rounded mb-2 cursor-pointer ${
                                                    selectedVersion?.id === version.id ? 'bg-primary text-white' : 'bg-light'
                                                }`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSelectVersion(version)}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h6 className={`mb-1 ${selectedVersion?.id === version.id ? 'text-white' : 'text-primary'}`}>
                                                            Version {version.version}
                                                        </h6>
                                                        <small className={selectedVersion?.id === version.id ? 'text-white-50' : 'text-muted'}>
                                                            {formatDate(version.publishedDate)}
                                                        </small>
                                                    </div>
                                                    {selectedVersion?.id === version.id && (
                                                        <i className="ri-check-line text-white"></i>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Right side - Version details */}
                        <div className="col-md-9">
                            <div className="ps-3">
                                <h6 className="mb-3">Version Details</h6>
                                {!selectedVersion ? (
                                    <div className="text-center py-5">
                                        <i className="ri-information-line text-muted" style={{ fontSize: '48px' }}></i>
                                        <p className="text-muted mt-3 mb-0">Select a version from the left to view details</p>
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
                                        <div  style={{ marginRight: '5px' }}>
                                        <div className="row g-3 mb-3">
                                            <div className="col-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Version</label>
                                                    <div className="bg-light p-2 rounded">
                                                        <Badge color="primary">v{selectedVersion.version}</Badge>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Published Date</label>
                                                    <div className="bg-light p-2 rounded">
                                                        {formatDate(selectedVersion.publishedDate)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="row g-3 mb-3">
                                            <div className="col-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Title</label>
                                                    <div className="bg-light p-2 rounded">
                                                        {selectedVersion.title || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="mb-3">
                                                    <label className="form-label fw-semibold">Status</label>
                                                    <div className="bg-light p-2 rounded">
                                                        {getStatusBadge(selectedVersion.status)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Description</label>
                                            <div className="bg-light p-2 rounded">
                                                {selectedVersion.description || 'No description provided'}
                                            </div>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Endpoint Slug</label>
                                            <div className="bg-light p-2 rounded">
                                                <code>{selectedVersion.endpointSlug}</code>
                                            </div>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">LLM Configuration</label>
                                            <div className="bg-light p-2 rounded">
                                                LLM ID: {selectedVersion.llmId}
                                            </div>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Prompt</label>
                                            <div className="bg-light p-3 rounded" style={{ maxHeight: '150px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', wordBreak: 'break-word' }}>
                                                    {selectedVersion.prompt || 'No prompt configured'}
                                                </pre>
                                            </div>
                                        </div>
                                        
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">JSON Format Data</label>
                                            <div className="bg-light p-3 rounded" style={{ maxHeight: '150px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', wordBreak: 'break-word' }}>
                                                    {formatJsonData(selectedVersion.jsonFormatData)}
                                                </pre>
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={closeVersionModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal} centered>
                <ModalHeader toggle={closeDeleteModal}>
                    Delete or Deactivate Endpoint
                </ModalHeader>
                <ModalBody>
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="ri-delete-bin-line text-warning" style={{ fontSize: '48px' }}></i>
                        </div>
                        <h5 className="mb-3">What would you like to do with this endpoint?</h5>
                        {endpointToDelete && (
                            <div className="text-muted mb-4">
                                <p className="mb-1"><strong>Title:</strong> {endpointToDelete.title}</p>
                                <p className="mb-1"><strong>Slug:</strong> {endpointToDelete.endpointSlug}</p>
                            </div>
                        )}
                        <div className="row text-start">
                            <div className="col-md-6">
                                <div className="p-3 border rounded">
                                    <h6 className="text-warning mb-2">
                                        <i className="ri-pause-circle-line me-2"></i>
                                        Inactive Only
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Temporarily disable the endpoint. Can be reactivated later.
                                    </p>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="p-3 border rounded">
                                    <h6 className="text-danger mb-2">
                                        <i className="ri-delete-bin-line me-2"></i>
                                        Permanent Delete
                                    </h6>
                                    <p className="text-muted small mb-0">
                                        Permanently remove the endpoint. This cannot be undone.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter className="justify-content-between">
                    <Button 
                        color="light" 
                        onClick={closeDeleteModal}
                        disabled={isDeleting || isInactivating}
                    >
                        Cancel
                    </Button>
                    
                    <div className="d-flex gap-2">
                        <Button 
                            color="warning" 
                            onClick={handleInactiveEndpoint}
                            disabled={isDeleting || isInactivating}
                        >
                            {isInactivating ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Inactivating...
                                </>
                            ) : (
                                <>
                                    <i className="ri-pause-circle-line me-1"></i>
                                    Inactive Only
                                </>
                            )}
                        </Button>
                        <Button 
                            color="danger" 
                            onClick={confirmDeleteEndpoint}
                            disabled={isDeleting || isInactivating}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-1"></i>
                                    Delete Permanently
                                </>
                            )}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>

            {/* Code Examples Modal */}
            <CodeExamplesModal
                isOpen={isCodeExamplesModalOpen}
                toggle={closeCodeExamplesModal}
                endpoint={endpointForCodeExamples}
            />

            {/* Help Modal */}
            <EndpointHelpModal
                isOpen={isHelpModalOpen}
                toggle={() => setIsHelpModalOpen(false)}
            />
        </>
    );
};

export default ProductionEndpointsTable;